import { AGENT_IDS, PERSONAS, fmtEth, shortAddr, type ProtocolEvent, type PublicConfig } from '@quintessence/core';
import { actionHeadline, ago, sym, txUrl } from '../lib';
import { CHART_ORDER, SERIES } from '../theme';
import { AgentAvatar } from './AgentAvatar';
import { Icon } from './icons';

function TxLink({ config, hash }: { config: PublicConfig; hash: string | undefined }) {
  if (!hash) return null;
  const url = txUrl(config, hash);
  return url ? (
    <a className="tx" href={url} target="_blank" rel="noreferrer">
      {shortAddr(hash)} <Icon name="external" size={12} />
    </a>
  ) : (
    <span className="tx tx-sim" title="Simulated transaction">
      sim · {shortAddr(hash)}
    </span>
  );
}

function SystemGlyph({ name }: { name: 'scale' | 'book' | 'dna' | 'bolt' | 'spark' }) {
  return (
    <span className="sys-glyph" aria-hidden>
      <Icon name={name} size={18} />
    </span>
  );
}

export function FeedItem({ e, now, config }: { e: ProtocolEvent; now: number; config: PublicConfig }) {
  const symbol = sym(config);
  switch (e.type) {
    case 'action': {
      const p = PERSONAS[e.agent];
      return (
        <li className="feed-item">
          <AgentAvatar id={e.agent} size={36} />
          <div className="feed-body">
            <div className="feed-line">
              <strong>{p.name}</strong> {actionHeadline(e, symbol)}
            </div>
            <div className="feed-meta">
              {fmtEth(e.result.ethSpent)} ETH · {e.size.toLowerCase()} · {e.thought.regime} · {Math.round(e.thought.confidence * 100)}% confident
              {e.thought.explored && <span className="chip chip-quiet">experiment</span>}
            </div>
            <p className="feed-thought">“{e.thought.summary}”</p>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
              <TxLink config={config} hash={e.result.txHashes[e.result.txHashes.length - 1]} />
            </div>
          </div>
        </li>
      );
    }
    case 'harvest':
      return (
        <li className="feed-item">
          <SystemGlyph name="bolt" />
          <div className="feed-body">
            <div className="feed-line">
              <strong>Treasury</strong> harvested {fmtEth(e.result.total)} ETH of fees
            </div>
            <div className="split-bar" aria-label="Split across agents">
              {CHART_ORDER.map((id) => {
                const i = AGENT_IDS.indexOf(id);
                const share = e.result.total > 0 ? (e.result.amounts[i] / e.result.total) * 100 : 25;
                return <span key={id} style={{ flex: `${share} 1 0`, background: SERIES[id] }} title={`${PERSONAS[id].name} ${fmtEth(e.result.amounts[i])} ETH`} />;
              })}
            </div>
            <div className="feed-meta">
              {AGENT_IDS.map((id, i) => `${PERSONAS[id].name} ${fmtEth(e.result.amounts[i])}`).join(' · ')}
            </div>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
              <TxLink config={config} hash={e.result.txHash} />
            </div>
          </div>
        </li>
      );
    case 'council':
      return (
        <li className="feed-item">
          <SystemGlyph name="scale" />
          <div className="feed-body">
            <div className="feed-line">
              <strong>Council</strong> re-weighted the fee split
            </div>
            <div className="feed-meta">
              {AGENT_IDS.map((id, i) => {
                const d = e.weights[i] - e.previous[i];
                return (
                  <span key={id} className="weight-chip">
                    {PERSONAS[id].name} {(e.weights[i] / 100).toFixed(1)}%
                    {d !== 0 && <em className={d > 0 ? 'good' : 'bad'}> {d > 0 ? '▲' : '▼'}{Math.abs(d / 100).toFixed(1)}</em>}
                  </span>
                );
              })}
            </div>
            <p className="feed-thought">{e.rationale}</p>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
              <TxLink config={config} hash={e.txHash} />
            </div>
          </div>
        </li>
      );
    case 'lesson':
      return (
        <li className="feed-item">
          <AgentAvatar id={e.agent} size={36} />
          <div className="feed-body">
            <div className="feed-line">
              <strong>{PERSONAS[e.agent].name}</strong> learned a lesson
              <span className="chip">{Math.round(e.lesson.confidence * 100)}% confidence</span>
            </div>
            <p className="feed-thought">{e.lesson.text}</p>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
            </div>
          </div>
        </li>
      );
    case 'evolution':
      return (
        <li className="feed-item">
          <SystemGlyph name="dna" />
          <div className="feed-body">
            <div className="feed-line">
              <strong>{PERSONAS[e.agent].name}</strong> evolved to generation {e.generation}
            </div>
            <div className="feed-meta">
              Genome #{e.replacedId} retired · #{e.childId} born from champion #{e.championId}
            </div>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
            </div>
          </div>
        </li>
      );
    case 'graduation':
      return (
        <li className="feed-item">
          <SystemGlyph name="spark" />
          <div className="feed-body">
            <div className="feed-line">
              <strong>{symbol}</strong> graduated into its Uniswap V4 pool
            </div>
            <div className="feed-meta">TIDE is awake: liquidity adds are now possible.</div>
            <div className="feed-foot">
              <span>{ago(e.t, now)}</span>
            </div>
          </div>
        </li>
      );
    default:
      return null;
  }
}

export function Feed({ events, now, config, empty = 'Waiting for the first on-chain action…' }: { events: ProtocolEvent[]; now: number; config: PublicConfig; empty?: string }) {
  if (!events.length) return <p className="empty">{empty}</p>;
  return (
    <ul className="feed">
      {events.map((e) => (
        <FeedItem key={e.id} e={e} now={now} config={config} />
      ))}
    </ul>
  );
}
